import { NotetakerBlockedDomain } from '@/types'

/** Mirrors `MAX_NOTETAKER_BLOCKED_ENTRIES` in aria-web. */
export const MAX_BLOCKED_DOMAIN_ENTRIES = 200

/**
 * Reduce an entry to the key the server would store it under, for local diffing only.
 * Mirrors `NotetakerBlockedPrincipal.from_entry`: `*@acme.com`, `@acme.com` and
 * `acme.com` are one rule, and a trailing dot is noise.
 *
 * Comparison only — `entries` still goes to the server verbatim, so this classifies
 * nothing. Parity is approximate: IDNs are punycoded server-side, so a unicode
 * spelling and its ascii form still read as two entries here.
 */
function comparisonKey(entry: string): string {
  const text = entry.trim().toLowerCase()
  const stripTrailingDots = (value: string) => value.replace(/\.+$/, '')

  if (text.startsWith('*@')) return stripTrailingDots(text.slice(2))
  if (text.startsWith('@')) return stripTrailingDots(text.slice(1))

  const at = text.lastIndexOf('@')
  if (at === -1) return stripTrailingDots(text)
  return `${text.slice(0, at)}@${stripTrailingDots(text.slice(at + 1))}`
}

/**
 * Split a comma/newline-separated textarea into raw entries, de-duplicated by
 * canonical key in first-seen order, keeping the spelling the admin typed.
 *
 * Deliberately does no classifying or validating: the server is the only
 * classifier, so anything decided here could disagree with what gets stored.
 */
export function parseBlockedDomainEntries(input: string): string[] {
  const seen = new Set<string>()
  const entries: string[] = []
  for (const token of input.split(/[\n,]/)) {
    const trimmed = token.trim()
    if (!trimmed) continue
    const key = comparisonKey(trimmed)
    if (seen.has(key)) continue
    seen.add(key)
    entries.push(trimmed)
  }
  return entries
}

/**
 * Render stored rules back into textarea text. Comma-separated to match the design
 * and the field's own instruction; the parser accepts newlines too.
 */
export function entriesToText(rules: NotetakerBlockedDomain[]): string {
  return rules.map((rule) => rule.value).join(', ')
}

/** The canonical values of the stored rules, keyed for comparison. */
export function storedValues(rules: NotetakerBlockedDomain[]): string[] {
  return rules.map((rule) => comparisonKey(rule.value))
}

/** Order-independent equality against the stored rules' canonical values. */
export function matchesStored(entries: string[], rules: NotetakerBlockedDomain[]): boolean {
  if (entries.length !== rules.length) return false
  const stored = new Set(storedValues(rules))
  return entries.every((entry) => stored.has(comparisonKey(entry)))
}

/**
 * Whether the edit only drops entries. The backend keeps removal and wind-down
 * allowed once the rollout gate is off, so an org that loses the gate can still
 * unblock itself — this decides when to permit a save in that state.
 */
export function isRemovalOnly(entries: string[], rules: NotetakerBlockedDomain[]): boolean {
  const stored = new Set(storedValues(rules))
  return entries.every((entry) => stored.has(comparisonKey(entry)))
}

/** Entries the edit adds relative to what is stored, for the gate-off message. */
export function addedEntries(entries: string[], rules: NotetakerBlockedDomain[]): string[] {
  const stored = new Set(storedValues(rules))
  return entries.filter((entry) => !stored.has(comparisonKey(entry)))
}

/** `3 rules` / `1 rule`, for labels and counts. */
export function ruleCountLabel(count: number): string {
  return `${count} rule${count === 1 ? '' : 's'}`
}
